import type { User } from '../../types/User';

/**
 * Perfis do Cognito (claim `cognito:groups`).
 *
 * Antes do AF-3649 estas strings viviam como `const` dentro da função AppRoutes, sem export,
 * então qualquer tela que precisasse checar perfil tinha que redeclarar a string. Centralizar
 * aqui é o que permite gatear ações dentro das páginas, e não só o acesso à rota.
 */
export const ROLES = {
    ADMIN: 'ADMIN',
    OPERATOR: 'OPERATOR',
    LEGAL: 'LEGAL',
    CREDIT_ANALYST: 'CREDIT_ANALYST',
    CREDIT_MANAGER: 'CREDIT_MANAGER',
    FINANCIAL: 'FINANCIAL',
    CONTRACT_OPERATIONS_ANALYST: 'CONTRACT_OPERATIONS_ANALYST',
    // Grupo Cognito "Registros de PF" (descrição no Cognito). Habilita a edição do
    // cadastro de pessoa (PF/PJ) junto com ADMIN. O valor anterior INDIVIDUAL_PERSON
    // era fantasma — não existe grupo com esse nome em nenhum ambiente (AF-4091).
    INDIVIDUAL_REGISTRATION: 'INDIVIDUAL_REGISTRATION',
    COLLECTION_ANALYST: 'COLLECTION_ANALYST',
    BLOCKLIST: 'BLOCKLIST',
    MLP_ANALYST: 'MLP_ANALYST',
    COMMERCIAL_MANAGER: 'COMMERCIAL_MANAGER',
    // Grupo dedicado à Tesouraria (funding) - antes usava FINANCIAL, mas esse
    // grupo é compartilhado com outras telas (Dimensa/financeiro). Criado no
    // Cognito de DEV em 2026-09-11, só ADMIN e FUNDING_MANAGER acessam.
    FUNDING_MANAGER: 'FUNDING_MANAGER',
    // Grupos de acesso da Custódia B3 (AF-3830). Criados manualmente no Cognito.
    // ADMIN da B3 acessa tudo (inclusive Configurações e telas de funding); o
    // operador de crédito acessa as telas de custódia (títulos, operações,
    // direitos creditórios, instrumentos CPRF, remessas) sem Configurações; o
    // operador de funding acessa apenas as telas de funding e remessas.
    B3_CUSTODY_ADMIN: 'B3-CUSTODY-ADMIN',
    B3_CUSTODY_CREDIT_OPERATOR: 'B3-CUSTODY-CREDIT-OPERATOR',
    B3_CUSTODY_FUNDING_OPERATOR: 'B3-CUSTODY-FUNDING-OPERATOR',
} as const;

export type Role = typeof ROLES[keyof typeof ROLES];

/** Todos os perfis: para telas abertas a qualquer usuário logado, com as ações gateadas dentro da página. */
export const ALL_ROLES: string[] = Object.values(ROLES);

/**
 * Telas de Financeiro e Cobrança do billing (AF-4143). O menu e a rota de cada tela usam a mesma
 * lista, para os dois não divergirem.
 */
export const FINANCIAL_SCREENS_ROLES: readonly string[] = [ROLES.ADMIN, ROLES.FINANCIAL];
export const COLLECTION_SCREENS_ROLES: readonly string[] = [ROLES.ADMIN, ROLES.COLLECTION_ANALYST];
/** Títulos: a Cobrança pelo menu, e o Financeiro pela "Listagem de Títulos" das Operações de Crédito. */
export const CHARGES_ROUTE_ROLES: readonly string[] = [...COLLECTION_SCREENS_ROLES, ROLES.FINANCIAL];

/** Se o usuário possui ao menos um dos perfis exigidos. */
export const hasAnyRole = (user: User | null | undefined, roles: readonly string[]): boolean => {
    if (!user || !user.roles || roles.length === 0) return false;
    return user.roles.some(role => roles.includes(role));
};

/**
 * Quem pode enviar um contrato para assinatura (AF-3649).
 * Decisão de negócio: apenas o jurídico envia. ADMIN mantém o acesso, OPERATOR não envia.
 */
export const canSendContractToSignature = (user: User | null | undefined): boolean => {
    return hasAnyRole(user, [ROLES.ADMIN, ROLES.LEGAL]);
};

/**
 * Quem age sobre um processo de assinatura na tela de detalhes (AF-4138): enviar, reenviar,
 * congelar, resgatar, encerrar, cancelar, notificar e corrigir signatário. Qualquer outro perfil
 * logado só acompanha: "Atualizar status" e o download continuam.
 * Só o front esconde as ações, sem bloqueio no Router (decisão do dev, risco aceito).
 */
export const canActOnSignatureProcess = (user: User | null | undefined): boolean => {
    return hasAnyRole(user, [ROLES.ADMIN, ROLES.LEGAL]);
};

/**
 * Quem pode criar e editar operações.
 * LEGAL acessa a lista de contratos apenas para leitura — as rotas `operations/*` não
 * liberam LEGAL, então as ações que navegam para lá ficam escondidas para esse perfil.
 */
export const canManageOperations = (user: User | null | undefined): boolean => {
    return hasAnyRole(user, [ROLES.ADMIN, ROLES.OPERATOR]);
};

/**
 * Quem vê as ações Gerenciar e Nova Operação na lista de contratos (AF-4120).
 * Decisão de negócio: quem tem LEGAL não gerencia operações pela lista, mesmo acumulando
 * OPERATOR. ADMIN mantém o acesso. Os menus e as rotas `operations/*` seguem em
 * `canManageOperations`.
 */
export const canManageOperationsFromContractList = (user: User | null | undefined): boolean => {
    if (hasAnyRole(user, [ROLES.ADMIN])) return true;
    return hasAnyRole(user, [ROLES.OPERATOR]) && !hasAnyRole(user, [ROLES.LEGAL]);
};

/**
 * Quem acessa os Tipos de Contrato (lista e edição).
 * O jurídico entra aqui além dos perfis operacionais (AF-3649).
 */
export const canAccessContractTypes = (user: User | null | undefined): boolean => {
    return hasAnyRole(user, [ROLES.ADMIN, ROLES.OPERATOR, ROLES.LEGAL]);
};

/** Usuário que só tem o perfil jurídico, sem nenhum perfil operacional acumulado. */
export const isLegalOnly = (user: User | null | undefined): boolean => {
    return hasAnyRole(user, [ROLES.LEGAL]) && !canManageOperations(user);
};

/**
 * Quem pode EDITAR (escrever) o cadastro de pessoa, PF e PJ (AF-4091).
 * Decisão de negócio: apenas ADMIN e INDIVIDUAL_REGISTRATION editam o cadastro.
 * LEGAL (jurídico) e OPERATOR apenas visualizam — OPERATOR nasce em todas as contas
 * e não pode escrever cadastro. As telas derivam o read-only deste helper, então o
 * modo leitura vale mesmo que o usuário remova `?readOnly=true` da URL.
 */
export const canEditPerson = (user: User | null | undefined): boolean => {
    return hasAnyRole(user, [ROLES.ADMIN, ROLES.INDIVIDUAL_REGISTRATION]);
};

/**
 * Custódia B3 (AF-3830). O acesso é por tela, mapeado a três grupos Cognito
 * (mais o ADMIN legado, que enxerga tudo). O backend expõe b3-custody como uma
 * rota única, então a separação por tela vive só aqui e no gate de rotas.
 */

/** Configurações da Custódia B3 (produtos, commodities, safras, emitentes). */
export const canAccessB3Configuration = (user: User | null | undefined): boolean => {
    return hasAnyRole(user, [ROLES.ADMIN, ROLES.B3_CUSTODY_ADMIN]);
};

/**
 * Telas de custódia (Títulos, Operações, Direitos Creditórios, Instrumentos CPRF).
 * O operador de crédito entra aqui, mas não nas Configurações.
 */
export const canAccessB3CustodyScreens = (user: User | null | undefined): boolean => {
    return hasAnyRole(user, [
        ROLES.ADMIN,
        ROLES.B3_CUSTODY_ADMIN,
        ROLES.B3_CUSTODY_CREDIT_OPERATOR,
    ]);
};

/** Remessas — visível para os três grupos da custódia. */
export const canAccessB3Remittances = (user: User | null | undefined): boolean => {
    return hasAnyRole(user, [
        ROLES.ADMIN,
        ROLES.B3_CUSTODY_ADMIN,
        ROLES.B3_CUSTODY_CREDIT_OPERATOR,
        ROLES.B3_CUSTODY_FUNDING_OPERATOR,
    ]);
};

/**
 * Telas de funding. Além dos grupos históricos (ADMIN, FUNDING_MANAGER), os
 * grupos da custódia B3 com escopo de funding também entram (AF-3830).
 */
export const canAccessB3Funding = (user: User | null | undefined): boolean => {
    return hasAnyRole(user, [
        ROLES.ADMIN,
        ROLES.FUNDING_MANAGER,
        ROLES.B3_CUSTODY_ADMIN,
        ROLES.B3_CUSTODY_FUNDING_OPERATOR,
    ]);
};

/** Menus do Financeiro: Dimensa, Operações de Crédito, Contas de Recebimento e Pagamentos (AF-4143). */
export const canAccessFinancialScreens = (user: User | null | undefined): boolean =>
    hasAnyRole(user, FINANCIAL_SCREENS_ROLES);

/** Menus da Cobrança: Títulos, Faturas e Identificação de Pagamento (AF-4143). */
export const canAccessCollectionScreens = (user: User | null | undefined): boolean =>
    hasAnyRole(user, COLLECTION_SCREENS_ROLES);

/**
 * Quem age sobre as faturas no modal de faturas do título (AF-4143): sinalizar, liquidar, cobrar,
 * cancelar e gerar fatura são da cobrança. O financeiro, que chega a Títulos pela Operações de
 * Crédito, só consulta Detalhes e Boleto.
 */
export const canManageInvoices = (user: User | null | undefined): boolean =>
    hasAnyRole(user, COLLECTION_SCREENS_ROLES);
